import { Component, Input, OnInit, OnDestroy } from '@angular/core';
import { RemoteControlDeviceState } from 'app/modules/remote-control/services/device-state-registry.service';
import { MatCheckboxChange } from '@angular/material/checkbox';
import { SotiPopupTrigger } from '@soti/popup';
import { RemoteControlSessionService } from 'xsmodules/remote-control/services/session.service';
import { RcLogsService, DeviceLogRequestStatus } from 'xsmodules/remote-control/services/rc-logs.service';

@Component({
  selector: 'app-remote-control-device-logs-selector',
  templateUrl: './remote-control-device-logs-selector.component.html',
  styleUrls: ['./remote-control-device-logs-selector.component.scss'],
})
export class RemoteControlDeviceLogsSelectorComponent implements OnInit, OnDestroy {
  @Input() rcState: RemoteControlDeviceState;
  @Input() popUpTrigger: SotiPopupTrigger;
  popupClosedSubscription: any;

  public get selectedCategoriesCount(): number {
    // Get the values from the list of categories and count only the checked ones
    return Array.from(this.categories.values()).filter(c => c).length;
  }

  public get totalCategoriesCount(): number {
    return this.categories.size;
  }

  public get isDownloadEnabled(): boolean {
    return this.selectedCategoriesCount > 0;
  }

  public get isSelected(): boolean {
    // We consider the button selected as long as something is happenning with the device logs download
    return this.isOpened || this.rcLogsService.getDeviceLogRequestStatus() !== DeviceLogRequestStatus.None;
  }

  public categories: Map<string, boolean> = new Map<string, boolean>();

  private isOpened = false;

  constructor(private rcLogsService: RcLogsService, private remoteControlSessionService: RemoteControlSessionService) { }

  trackByKey(index: number, category): string {
    return category.key;
  }

  ngOnInit(): void {
    this.popupClosedSubscription = this.popUpTrigger.popUpClosed.subscribe(this.onPopupClosed);
  }

  ngOnDestroy(): void {
    if (this.popupClosedSubscription) {
      this.popupClosedSubscription.unsubscribe();
    }
  }

  public open = () => {
    switch (this.rcLogsService.getDeviceLogRequestStatus()) {
      case DeviceLogRequestStatus.None: {
        if (this.remoteControlSessionService.isDeviceLogCategoriesSupported) {
          this.rcLogsService
            .requestDeviceLogsCategories()
            .then((categoryMap: Map<string, boolean>) => {
              this.categories = categoryMap;
            })
            .catch((error: any) => {
              console.error('Error getting the device logs categories', error);
            });

          this.popUpTrigger.open();
          this.isOpened = true;
        } else {
          // Download device logs without specific categories (old behavior)
          this.rcLogsService.downloadDeviceLogs();
        }
        break;
      }

      case DeviceLogRequestStatus.Delayed: {
        this.rcLogsService.cancelDeviceLogsRequest();
        break;
      }

      case DeviceLogRequestStatus.Pending:
      case DeviceLogRequestStatus.Attaching: {
        console.log('There is already a pending request, Device Logs cannot be canceled or downloaded at this time');
        break;
      }
    }
  };

  onPopupClosed = (): void => {
    this.popupCleanup();
    this.isOpened = false;
  };

  /**
   * Checks if a category is currently checked
   *
   * @param categoryName the name to verify if it is checked or not
   * @returns the current checked status of the category
   */
  public isCategoryChecked = (categoryName: string): boolean => this.categories.get(categoryName);

  /**
   * Called when a category checkbox is checked/unchecked
   *
   * @param categoryName The category that was modified
   * @param change the new status of the category
   */
  public onCategoryChanged = (categoryName: string, change: MatCheckboxChange) => {
    this.categories.set(categoryName, change.checked);
  };

  /**
   * Called when the DOWNLOAD button is pressed
   */
  public onDownloadClicked = (): void => {
    console.log('Download clicked');

    // Get only the selected categories...
    const selectedCategories = Array.from(this.categories.keys()).filter(c => this.categories.get(c));

    this.rcLogsService.downloadDeviceLogs(selectedCategories);

    this.popupCleanup();
  };

  /**
   * Called when the CANCEL button is pressed
   */
  public onCancelClicked = (): void => {
    this.popupCleanup();
  };

  private popupCleanup() {
    this.popUpTrigger.close();
    this.categories.clear();
  }
}
